import React, { ReactNodeArray } from 'react';

import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';

import {
  CellContent,
  EntityCell,
  EntityInfoCell,
  LabelName,
  SecondaryValue,
  ValueWrapper,
} from './DoubleMetricCell.style';

import { ABBREVIATION_THRESHOLD } from 'helpers/abbreviateNumber';
import formatNumber from 'helpers/formatNumber';
import getNumberColor from 'helpers/getNumberColor';

import { EntityTypes } from 'context/entityContext';
import { IMetric } from 'maps/metrics';
import { ReactElementLike } from 'prop-types';

type TTooltipContent =
  | string
  | number
  | boolean
  | Record<string, unknown>
  | ReactElementLike
  | ReactNodeArray;

interface IDoubleMetricCellProps {
  browseEntityType?: EntityTypes;
  className?: string;
  tableName?: string;
  entityData: any;
  header: any;
  sortBy: IMetric;
  hideSecondary?: boolean;
  paddingRight?: string;
  getTooltipContent?: () => TTooltipContent;
  isInTable?: boolean;
  isCentered?: boolean;
}

/**
 * This component displays primary and secondary metrics in a single cell.
 * The active sorting metric determines the primary value and is visually more prominent.
 */
const DoubleMetricCell: React.FC<IDoubleMetricCellProps> = (props) => {
  const {
    browseEntityType,
    className,
    entityData,
    header,
    sortBy,
    hideSecondary,
    tableName,
    paddingRight,
    isInTable = false,
    isCentered,
  } = props;
  let { getTooltipContent } = props;

  const isSpecificView =
    tableName === 'BrowseOpus' || tableName === 'customLists';
  const isCustomSortBy = sortBy.value === 'custom' && isSpecificView;
  const twtd: any = {
    label: 'This week to date',
    value: 'performance_this_week_to_date',
    key: 'performance_this_week_to_date',
    range: 'performance_this_week_to_date_range',
    change: 'performance_wow_in_progress',
  };
  const sortByMetric = isSpecificView && isCustomSortBy ? twtd : sortBy;
  const headerMetric = isSpecificView && isCustomSortBy ? twtd : header;
  const changeIsPrimary =
    sortByMetric &&
    header &&
    sortByMetric.key &&
    sortByMetric.key === headerMetric.change;
  const notLabels = browseEntityType !== 'label';
  const hasSecondary =
    !hideSecondary &&
    notLabels &&
    sortByMetric &&
    headerMetric &&
    'change' in headerMetric;
  const primaryFormat =
    header.value === 'twtdOfGlobal' || header.value === 'wowInProgress'
      ? 'percentage'
      : changeIsPrimary
      ? 'trend'
      : 'number';
  const secondaryFormat = changeIsPrimary ? 'number' : 'trend';
  const changeInPrimary =
    headerMetric.isRange && tableName === 'streamingTerritories';

  // All data related references are defined in `src/maps/metrics`.
  // When a count metric is selected (e.g 'This Week To Date (Count)'),
  // a `change` key (e.g 'performance_wow_last_completed') can exist if
  // the metric has a secondary trend metric. The value of this key is
  // the same as key in the API response to get the actual data value.
  //
  // This can also happen the other way around, when a change metric is
  // selected (e.g 'This Week To Date (Change)'), a `count` key can exist
  // to reference its complementary count metric.
  //
  // The table headers never change but the presence of either of these keys
  // can be used to compare it to the currently active sorting metric, which can
  // be a different object from `maps/metrics` than the ones used for the table headers,
  // to know which one is primary.
  const secondary =
    sortByMetric &&
    headerMetric &&
    (changeIsPrimary
      ? entityData[sortByMetric.count || 0]
      : entityData[headerMetric.change]);
  const primary =
    sortByMetric &&
    headerMetric &&
    (changeIsPrimary
      ? entityData[sortByMetric.key]
      : entityData[headerMetric.key]);

  const primaryDisplayValue = formatNumber(primary, primaryFormat, true);
  if (!getTooltipContent) {
    getTooltipContent = (): TTooltipContent => {
      return formatNumber(primary, primaryFormat, false);
    };
  }
  const hasColor = getNumberColor(secondary);
  const EntityWrap = isSpecificView || isInTable ? EntityInfoCell : EntityCell;

  return (
    <EntityWrap
      key={header && `browse-data-cell-${header.key}`}
      data-cell-value={primary}
      className={className}
    >
      <CellContent
        paddingRight={paddingRight}
        isRightAligned
        isCentered={isCentered}
        justifyContent={
          tableName === 'streamingTerritories' ? 'flex-end' : 'center'
        }
      >
        {!changeInPrimary && (
          <ValueWrapper tableName={tableName}>
            <TippyToolTip
              tooltipContent={getTooltipContent()}
              enabled={Math.abs(primary) >= ABBREVIATION_THRESHOLD}
              canBeCopied
            >
              {isSpecificView && (
                <LabelName>
                  {hasSecondary
                    ? 'Streams'
                    : sortByMetric &&
                      sortByMetric.label.replace(/(\(\w+\s?\w+\s\w+\))/, '')}
                </LabelName>
              )}
              <span>{primaryDisplayValue}</span>
            </TippyToolTip>
          </ValueWrapper>
        )}

        {(hasSecondary || changeInPrimary) && (
          <ValueWrapper>
            {isSpecificView && <LabelName>Change</LabelName>}
            <TippyToolTip
              tooltipContent={formatNumber(secondary, secondaryFormat, false)}
              canBeCopied
              enabled={
                secondary && Math.abs(secondary) >= ABBREVIATION_THRESHOLD
              }
            >
              <SecondaryValue hasColor={hasColor}>
                {secondary
                  ? formatNumber(secondary, secondaryFormat, true)
                  : '-'}
              </SecondaryValue>
            </TippyToolTip>
          </ValueWrapper>
        )}
      </CellContent>
    </EntityWrap>
  );
};

export default DoubleMetricCell;
